"use client";

import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type Metric = {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const METRICS: Metric[] = [
  { value: 500, suffix: "+", label: "Veículos gerenciados" },
  { value: 12, label: "Redes ativas" },
  { value: 3, suffix: "×", label: "Mais rápido que planilha" },
];

const COUNTER_DURATION_MS = 1400;

// ---------------------------------------------------------------------------
// CounterNumber — anima de 0 até value usando rAF ao entrar na viewport
// ---------------------------------------------------------------------------
const CounterNumber = ({
  value,
  prefix = "",
  suffix = "",
}: {
  value: number;
  prefix?: string;
  suffix?: string;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / COUNTER_DURATION_MS, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [isInView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

// ---------------------------------------------------------------------------
// MetricItem
// ---------------------------------------------------------------------------
const MetricItem = ({
  metric,
  index,
}: {
  metric: Metric;
  index: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center justify-center gap-1 px-8 py-6 sm:py-5 text-center"
    >
      <span className="text-3xl sm:text-4xl font-black tracking-tight text-foreground leading-none">
        <CounterNumber
          value={metric.value}
          prefix={metric.prefix}
          suffix={metric.suffix}
        />
      </span>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest whitespace-nowrap">
        {metric.label}
      </span>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// SocialProofBar
// ---------------------------------------------------------------------------
export default function SocialProofBar() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-40px" });

  return (
    <section
      ref={sectionRef}
      aria-label="Prova social e métricas"
      className="relative z-10 border-y border-border/50 bg-card/30 backdrop-blur-sm overflow-hidden"
    >
      {/* Glow sutil centralizado */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
      >
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[40%] h-[200%] bg-primary/5 rounded-full blur-[80px]" />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative">
        {/* Tagline central */}
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center text-sm font-medium text-muted-foreground pt-5 pb-1"
        >
          Mais de{" "}
          <span className="text-foreground font-semibold">500 veículos gerenciados</span>
          {" "}por redes em todo o Brasil
        </motion.p>

        {/* Métricas */}
        <div className="flex flex-col sm:flex-row items-stretch justify-center sm:divide-x divide-border/50 divide-y sm:divide-y-0">
          {METRICS.map((metric, i) => (
            <MetricItem key={metric.label} metric={metric} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
